import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from './firebaseConfig';
import './App.css';

const center = {
  lat: 34.5261,
  lng: -83.9844,
};

function MapComponent() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyDgp_z5Q-Cn0HRkKbwxciMZzxDE8-xT3S0',
  });

  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(null);

  const timeoutRef = useRef(null);
  const doubleTapRef = useRef(false);

  useEffect(() => {
    const loadMarkers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'camera_locations'));
        const loadedMarkers = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMarkers(loadedMarkers);
      } catch (error) {
        console.error('Error loading markers: ', error);
      }
    };

    loadMarkers();
  }, []);

  const addMarker = (lat, lng) => {
    const newMarker = {
      lat,
      lng,
      time: new Date(),
    };

    setSelectedMarker(newMarker);
  };

  const handleSaveGeotag = async (cameraType, direction, photo) => {
    if (!selectedMarker) return;

    const storage = getStorage();
    let photoUrl = '';

    if (photo) {
      const storageRef = ref(storage, `photos/${Date.now()}_${photo.name}`);
      await uploadBytes(storageRef, photo);
      photoUrl = await getDownloadURL(storageRef);
    }

    const newMarker = {
      ...selectedMarker,
      cameraType,
      direction,
      photoUrl,
    };

    try {
      const docRef = await addDoc(collection(db, 'camera_locations'), newMarker);
      setMarkers((current) => [...current, { id: docRef.id, ...newMarker }]);
      setSelectedMarker(null);
    } catch (error) {
      console.error('Error adding marker: ', error);
    }
  };

  const handleMapRightClick = (event) => {
    addMarker(event.latLng.lat(), event.latLng.lng());
  };

  const handleMapMouseDown = (event) => {
    timeoutRef.current = setTimeout(() => {
      addMarker(event.latLng.lat(), event.latLng.lng());
    }, 800);
  };

  const handleMapMouseUp = () => {
    clearTimeout(timeoutRef.current);
  };

  const handleMapClick = (event) => {
    if (doubleTapRef.current) {
      doubleTapRef.current = false;
      return;
    }

    timeoutRef.current = setTimeout(() => {
      doubleTapRef.current = true;
      addMarker(event.latLng.lat(), event.latLng.lng());
    }, 300);
  };

  if (loadError) return <div>Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div className="container">
      <div className="map-container" onContextMenu={(e) => e.preventDefault()}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          zoom={12}
          center={center}
          onRightClick={handleMapRightClick}
          onMouseDown={handleMapMouseDown}
          onMouseUp={handleMapMouseUp}
          onClick={handleMapClick}
        >
          {markers.map((marker) => (
            <Marker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              label={`${marker.cameraType}`}
              onClick={() => setInfoWindowOpen(marker.id)}
            >
              {infoWindowOpen === marker.id && (
                <InfoWindow
                  position={{ lat: marker.lat, lng: marker.lng }}
                  onCloseClick={() => setInfoWindowOpen(null)}
                >
                  <div>
                    <h3>{marker.cameraType}</h3>
                    <p>Direction: {marker.direction}</p>
                    {marker.photoUrl && <img src={marker.photoUrl} alt="Marker" style={{ width: '100px', height: '100px' }} />}
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
        </GoogleMap>
        {selectedMarker && (
          <GeotagForm
            onSave={handleSaveGeotag}
            onCancel={() => setSelectedMarker(null)}
          />
        )}
      </div>
      <div className="details-container">
        <h2>Camera Locations</h2>
        <ul>
          {markers.map((marker, index) => (
            <li key={marker.id}>
              <strong>Camera {index + 1}:</strong> {marker.cameraType}, {marker.direction}
              <br />
              Location: ({marker.lat.toFixed(4)}, {marker.lng.toFixed(4)})
              {marker.photoUrl && <img src={marker.photoUrl} alt="Marker" style={{ width: '100px', height: '100px', display: 'block', marginTop: '10px' }} />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function GeotagForm({ onSave, onCancel }) {
  const [cameraType, setCameraType] = useState('');
  const [direction, setDirection] = useState('');
  const [photo, setPhoto] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(cameraType, direction, photo);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        position: 'absolute',
        top: 10,
        right: '52%',
        background: '#fff',
        padding: '10px',
        borderRadius: '5px',
      }}
    >
      <h3>Add Geotag Information</h3>
      <label>
        Camera Type:
        <input
          value={cameraType}
          onChange={(e) => setCameraType(e.target.value)}
          required
        />
      </label>
      <br />
      <label>
        Direction:
        <input
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          required
        />
      </label>
      <br />
      <label>
        Upload Photo:
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files[0])}
        />
      </label>
      <br />
      <button type="submit">Save Geotag</button>
      <button
        type="button"
        onClick={onCancel}
        style={{ marginLeft: '10px' }}
      >
        Cancel
      </button>
    </form>
  );
}

function App() {
  return (
    <div>
      <h1>Flock LPR/Vehicle Tracking Camera</h1>
      <MapComponent />
    </div>
  );
}

export default App;
